export const transformKundliData = (rawData: any) => {
    if (!rawData) return null;

    // Create a mapping from backend chart names to frontend D-keys
    const chartNameToKey: { [key: string]: string } = {
        'Lagna': 'D1',
        'Hora': 'D2',
        'Drekkana': 'D3',
        'Chaturthamsa': 'D4',
        'Saptamsa': 'D7',
        'Navamsa': 'D9',
        'Dasamsa': 'D10',
        'Dwadasamsa': 'D12',
        'Shodasamsa': 'D16',
        'Vimsamsa': 'D20',
        'Chaturvimsamsa': 'D24',
        'Saptavimsamsa': 'D27',
        'Trimsamsa': 'D30',
        'Khavedamsa': 'D40',
        'Akshavedamsa': 'D45',
        'Shastiamsa': 'D60'
    };

    // Create a new structure with D1, D2, etc. keys
    const transformedData: any = { ...rawData };

    // Add ascendant info to D1 for consistency (from rawData.ascendant)
    if (rawData.ascendant) {
        transformedData.D1 = transformedData.D1 || {};
        transformedData.D1.ascendant = rawData.ascendant;
    }

    // Transform divisional charts to use D1, D2, etc. keys
    if (rawData.divisional_charts) {
        Object.entries(rawData.divisional_charts).forEach(([chartName, chartData]: [string, any]) => {
            const dKey = chartNameToKey[chartName];
            if (dKey && chartData) {
                transformedData[dKey] = {
                    ...transformedData[dKey],
                    ...chartData
                };
            }
        });
    }

    return transformedData;
};

export const chartMapping: { [key: string]: string } = {
    'D1': 'Lagna',
    'D2': 'Hora',
    'D3': 'Drekkana',
    'D4': 'Chaturthamsa',
    'D7': 'Saptamsa',
    'D9': 'Navamsa',
    'D10': 'Dasamsa',
    'D12': 'Dwadasamsa',
    'D16': 'Shodasamsa',
    'D20': 'Vimsamsa',
    'D24': 'Chaturvimsamsa',
    'D27': 'Saptavimsamsa',
    'D30': 'Trimsamsa',
    'D40': 'Khavedamsa',
    'D45': 'Akshavedamsa',
    'D60': 'Shastiamsa'
};

// Parse date string supporting both English and Hindi formats
export const parseDate = (dateStr: string): Date | null => {
    if (!dateStr) return null;

    // English month names (full and abbreviated)
    const englishMonths: { [key: string]: number } = {
        'january': 0, 'february': 1, 'march': 2, 'april': 3, 'may': 4, 'june': 5,
        'july': 6, 'august': 7, 'september': 8, 'october': 9, 'november': 10, 'december': 11,
        'jan': 0, 'feb': 1, 'mar': 2, 'apr': 3, 'jun': 5,
        'jul': 6, 'aug': 7, 'sep': 8, 'oct': 9, 'nov': 10, 'dec': 11
    };

    // Hindi month names
    const hindiMonths: { [key: string]: number } = {
        'जनवरी': 0, 'फरवरी': 1, 'मार्च': 2, 'अप्रैल': 3, 'मई': 4, 'जून': 5,
        'जुलाई': 6, 'अगस्त': 7, 'सितंबर': 8, 'अक्टूबर': 9, 'नवंबर': 10, 'दिसंबर': 11
    };

    // Combined months lookup
    const allMonths: { [key: string]: number } = { ...englishMonths, ...hindiMonths };

    // Try format: "7-January-2026" or "7-जनवरी-2026" (dash-separated)
    const dashMatch = dateStr.match(/(\d{1,2})-([^-]+)-(\d{4})/);
    if (dashMatch) {
        const day = parseInt(dashMatch[1]);
        const monthText = dashMatch[2].trim().toLowerCase();
        const year = parseInt(dashMatch[3]);
        // Try English first
        let month = allMonths[monthText];
        // If not found, try the original case (for Hindi)
        if (month === undefined) {
            month = allMonths[dashMatch[2].trim()];
        }
        if (month !== undefined) return new Date(year, month, day);
    }

    // Try English format with spaces: "12 Jan 2024"
    const spaceMatch = dateStr.match(/(\d{1,2})\s+(\w+)\s+(\d{4})/);
    if (spaceMatch) {
        const day = parseInt(spaceMatch[1]);
        const monthText = spaceMatch[2].trim().toLowerCase();
        const year = parseInt(spaceMatch[3]);
        let month = allMonths[monthText];
        if (month === undefined) {
            month = allMonths[spaceMatch[2].trim()];
        }
        if (month !== undefined) return new Date(year, month, day);
    }

    // Try ISO format: "2024-01-12"
    const isoMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoMatch) {
        return new Date(parseInt(isoMatch[1]), parseInt(isoMatch[2]) - 1, parseInt(isoMatch[3]));
    }

    // Try slash format: "12/01/2024"
    const slashMatch = dateStr.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (slashMatch) {
        return new Date(parseInt(slashMatch[3]), parseInt(slashMatch[2]) - 1, parseInt(slashMatch[1]));
    }

    const parsed = new Date(dateStr);
    return isNaN(parsed.getTime()) ? null : parsed;
};

// Check if a date is in the future
export const isFutureDate = (dateStr: string): boolean => {
    const date = parseDate(dateStr);
    if (!date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date > today;
};

export const isCurrentPeriod = (startDate: string, endDate: string): boolean => {
    try {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const parseDateInternal = (dateStr: string): Date | null => {
            if (!dateStr) return null;

            // English month names (full and abbreviated)
            const englishMonths: { [key: string]: number } = {
                'january': 0, 'february': 1, 'march': 2, 'april': 3, 'may': 4, 'june': 5,
                'july': 6, 'august': 7, 'september': 8, 'october': 9, 'november': 10, 'december': 11,
                'jan': 0, 'feb': 1, 'mar': 2, 'apr': 3, 'jun': 5,
                'jul': 6, 'aug': 7, 'sep': 8, 'oct': 9, 'nov': 10, 'dec': 11
            };

            // Hindi month names
            const hindiMonths: { [key: string]: number } = {
                'जनवरी': 0, 'फरवरी': 1, 'मार्च': 2, 'अप्रैल': 3, 'मई': 4, 'जून': 5,
                'जुलाई': 6, 'अगस्त': 7, 'सितंबर': 8, 'अक्टूबर': 9, 'नवंबर': 10, 'दिसंबर': 11
            };

            // Combined months lookup
            const allMonths: { [key: string]: number } = { ...englishMonths, ...hindiMonths };

            // Try format: "7-January-2026" or "7-जनवरी-2026" (dash-separated)
            const dashMatch = dateStr.match(/(\d{1,2})-([^-]+)-(\d{4})/);
            if (dashMatch) {
                const day = parseInt(dashMatch[1]);
                const monthText = dashMatch[2].trim().toLowerCase();
                const year = parseInt(dashMatch[3]);
                // Try English first
                let month = allMonths[monthText];
                // If not found, try the original case (for Hindi)
                if (month === undefined) {
                    month = allMonths[dashMatch[2].trim()];
                }
                if (month !== undefined) return new Date(year, month, day);
            }

            // Try English format with spaces: "12 Jan 2024"
            const spaceMatch = dateStr.match(/(\d{1,2})\s+(\w+)\s+(\d{4})/);
            if (spaceMatch) {
                const day = parseInt(spaceMatch[1]);
                const monthText = spaceMatch[2].trim().toLowerCase();
                const year = parseInt(spaceMatch[3]);
                let month = allMonths[monthText];
                if (month === undefined) {
                    month = allMonths[spaceMatch[2].trim()];
                }
                if (month !== undefined) return new Date(year, month, day);
            }

            // Try ISO format: "2024-01-12"
            const isoMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
            if (isoMatch) {
                return new Date(parseInt(isoMatch[1]), parseInt(isoMatch[2]) - 1, parseInt(isoMatch[3]));
            }

            // Try slash format: "12/01/2024"
            const slashMatch = dateStr.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
            if (slashMatch) {
                return new Date(parseInt(slashMatch[3]), parseInt(slashMatch[2]) - 1, parseInt(slashMatch[1]));
            }

            const parsed = new Date(dateStr);
            return isNaN(parsed.getTime()) ? null : parsed;
        };

        const start = parseDateInternal(startDate);
        const end = parseDateInternal(endDate);

        if (!start || !end) return false;

        return start <= today && today <= end;
    } catch (e) {
        return false;
    }
};

export const formatTime = (time: string) => {
    if (!time) return '---';
    try {
        const [h, m] = time.split(':');
        const hour = parseInt(h);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour % 12 || 12;
        return `${displayHour}:${m} ${ampm}`;
    } catch {
        return time;
    }
};
